/**
 * Build Leonardo Adaptive Theme share URLs (leonardocolor.io/theme.html?config=…)
 *
 * Format matches Leonardo web app "Copy theme URL" (see adobe/leonardo#211).
 * Uses RGB in the URL payload — OKLCH share URLs often hang on load in Leonardo web.
 */

import {
  BACKGROUND_COLORS,
  BASE_COLORS,
  resolveRatiosForTheme,
} from './constants';
import type { SimpleThemeConfig } from './theme';
import type { ThemeConfig } from './types';

/** Leonardo web app expects string ratios in the config JSON */
export interface LeonardoThemeUrlPayload {
  baseScale: string;
  colorScales: Array<{
    name: string;
    colorKeys: string[];
    colorspace: string;
    ratios: string[];
    smooth: boolean;
  }>;
  lightness: number;
  contrast: number;
  saturation: number;
  formula: 'wcag2' | 'wcag3';
}

const LEONARDO_WEB_COLORSPACE = 'RGB' as const;

export const themeConfigToLeonardoUrlPayload = (
  config: ThemeConfig,
  formula: 'wcag2' | 'wcag3' = 'wcag2',
  colorspace: typeof LEONARDO_WEB_COLORSPACE = LEONARDO_WEB_COLORSPACE
): LeonardoThemeUrlPayload => {
  const allScales = [config.backgroundColor, ...config.colors];

  return {
    baseScale: config.backgroundColor.name,
    colorScales: allScales.map((scale) => ({
      name: scale.name,
      colorKeys: scale.colorKeys.map(String),
      colorspace,
      ratios: scale.ratios.map(String),
      smooth: scale.smooth ?? false,
    })),
    lightness: config.lightness,
    contrast: config.contrast,
    saturation: config.saturation,
    formula,
  };
};

export const buildLeonardoThemeShareUrl = (
  themeName: string,
  config: ThemeConfig,
  formula: 'wcag2' | 'wcag3' = 'wcag2'
): string => {
  const payload = themeConfigToLeonardoUrlPayload(config, formula);
  const params = new URLSearchParams({
    name: themeName,
    config: JSON.stringify(payload),
  });

  return `https://leonardocolor.io/theme.html?${params.toString()}`;
};

/**
 * Build a share URL from the live site theme (ThemeContext / createTheme parity).
 * Mirrors scale names, smooth flags, and globals from createTheme().
 */
export const buildLeonardoShareUrlFromSimpleConfig = (
  themeName: string,
  config: Partial<SimpleThemeConfig>,
  colorMode: 'light' | 'dark'
): string => {
  const backgroundColor = config.backgroundColor ?? (colorMode === 'dark' ? 'Black' : 'White');
  const lightness =
    config.lightness ?? (colorMode === 'dark' ? 15 : 100);
  const contrast = config.contrast ?? 1;
  const saturation = config.saturation ?? 100;
  const formula = config.formula ?? 'wcag2';

  const ratios = resolveRatiosForTheme(config, colorMode).map(String);

  const colors = {
    gray: config.baseColors?.black ?? BASE_COLORS.black,
    red: config.baseColors?.red ?? BASE_COLORS.red,
    orange: config.baseColors?.orange ?? BASE_COLORS.orange,
    blue: config.baseColors?.blue ?? BASE_COLORS.blue,
  };

  const bgName = backgroundColor;
  const bgHex =
    backgroundColor === 'Black' ? BACKGROUND_COLORS.dark : BACKGROUND_COLORS.light;

  const payload: LeonardoThemeUrlPayload = {
    baseScale: bgName,
    colorScales: [
      {
        name: bgName,
        colorKeys: [bgHex],
        colorspace: LEONARDO_WEB_COLORSPACE,
        ratios,
        smooth: false,
      },
      {
        name: 'Gray',
        colorKeys: [colors.gray],
        colorspace: LEONARDO_WEB_COLORSPACE,
        ratios,
        smooth: false,
      },
      {
        name: 'Red',
        colorKeys: [colors.red],
        colorspace: LEONARDO_WEB_COLORSPACE,
        ratios,
        smooth: true,
      },
      {
        name: 'Orange',
        colorKeys: [colors.orange],
        colorspace: LEONARDO_WEB_COLORSPACE,
        ratios,
        smooth: false,
      },
      {
        name: 'Blue',
        colorKeys: [colors.blue],
        colorspace: LEONARDO_WEB_COLORSPACE,
        ratios,
        smooth: false,
      },
    ],
    lightness,
    contrast,
    saturation,
    formula,
  };

  const params = new URLSearchParams({
    name: themeName,
    config: JSON.stringify(payload),
  });

  return `https://leonardocolor.io/theme.html?${params.toString()}`;
};

export const SITE_LEONARDO_THEME_NAME = 'Color Scales I/O';
